import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";
import { MaterialCommunityIcons } from "@expo/vector-icons";

export type Market = {
  id: string;
  tenant_id: string;
  name: string;
  meta_data: Record<string, any>;
  fips_code: string;
};

export type MarketPickerProps = {
  selectedIds?: string[];
  onPick?: (marketId: string) => void;
  onMultiPick?: (marketIds: string[]) => void;
  onCancel?: () => void;
  multiple?: boolean;
};

export function MarketPicker({ selectedIds = [], onPick, onMultiPick, onCancel, multiple = false }: MarketPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMarketIds, setSelectedMarketIds] = useState<string[]>(selectedIds);

  // Fetch markets on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, tenant_id, name, meta_data, fips_code")
          .order("name", { ascending: true });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch markets:", fetchError);
          setError("Failed to load markets");
          return;
        }

        setMarkets(data ?? []);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching markets:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (!search) return markets;
    const searchLower = search.toLowerCase();
    return markets.filter(
      (market) =>
        market.name.toLowerCase().includes(searchLower) ||
        (market.fips_code && market.fips_code.toLowerCase().includes(searchLower)),
    );
  }, [markets, search]);

  const handleMarketSelect = (market: Market) => {
    if (multiple) {
      const isSelected = selectedMarketIds.includes(market.id);
      if (isSelected) {
        setSelectedMarketIds(selectedMarketIds.filter((id) => id !== market.id));
      } else {
        setSelectedMarketIds([...selectedMarketIds, market.id]);
      }
    } else {
      onPick?.(market.id);
    }
  };

  const handleConfirmMultiple = () => {
    onMultiPick?.(selectedMarketIds);
  };

  return (
    <View testID="market-picker" style={styles.container}>
      <ThemedText type="subtitle">{multiple ? "Select Markets" : "Select Market"}</ThemedText>

      <TextInput
        testID="market-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search markets..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredItems.length > 0 ? (
          filteredItems.map((market) => {
            const isSelected = selectedMarketIds.includes(market.id);
            return (
              <Pressable
                key={market.id}
                testID={`market-option-${market.id}`}
                style={[
                  styles.itemRow,
                  { borderBottomColor: colors.border },
                  multiple && isSelected && { backgroundColor: colors.primary + "10" },
                ]}
                onPress={() => handleMarketSelect(market)}
              >
                <View style={styles.itemContent}>
                  {multiple && (
                    <MaterialCommunityIcons
                      name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                      size={24}
                      color={isSelected ? colors.primary : colors.text}
                    />
                  )}
                  <View style={{ flex: 1 }}>
                    <ThemedText type="defaultSemiBold">{market.name}</ThemedText>
                    {market.fips_code && <ThemedText style={styles.fipsCode}>FIPS: {market.fips_code}</ThemedText>}
                  </View>
                </View>
              </Pressable>
            );
          })
        ) : (
          <ThemedText style={styles.message}>{search ? "No markets found" : "No markets available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        {multiple && (
          <>
            <ThemedText style={styles.selectedCount}>{selectedMarketIds.length} selected</ThemedText>
            <View style={styles.footerButtons}>
              {onCancel && (
                <Button
                  testID="market-picker-cancel"
                  text="Cancel"
                  style={[styles.cancelButton, { borderColor: colors.border }]}
                  textStyle={{ color: colors.text }}
                  onPress={onCancel}
                />
              )}
              <Button
                testID="market-picker-confirm"
                text="Confirm"
                style={styles.confirmButton}
                onPress={handleConfirmMultiple}
                enabled={selectedMarketIds.length > 0}
              />
            </View>
          </>
        )}
        {!multiple && onCancel && (
          <Button
            testID="market-picker-cancel"
            text="Cancel"
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 280,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 8,
  },
  itemRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  itemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  fipsCode: {
    fontSize: 12,
    color: "#4169E1",
    fontWeight: "500",
    marginTop: 2,
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
